import React, { useState, useEffect } from 'react';
import { Download, Send, CheckCircle, AlertCircle, Calendar, Euro, FileText, Users, Loader2, RefreshCcw } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const statusVariant = {
  generated: 'secondary',
  sent: 'warning',
  completed: 'success',
  failed: 'destructive',
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'generated': return <FileText className="h-4 w-4" />;
    case 'sent': return <Send className="h-4 w-4" />;
    case 'completed': return <CheckCircle className="h-4 w-4" />;
    case 'failed': return <AlertCircle className="h-4 w-4" />;
    default: return <FileText className="h-4 w-4" />;
  }
};

export default function SepaPaymentManager() {
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [sepaFiles, setSepaFiles] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [executionDate, setExecutionDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sepa');
      const payload = data.data || data;
      setPendingPayouts(payload.pending_payouts || []);
      setSepaFiles(payload.files || []);
      setSummary(payload.summary || null);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados de pagamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setExecutionDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const handleSelectPayout = (payoutId) => {
    setSelectedPayouts((prev) =>
      prev.includes(payoutId) ? prev.filter((id) => id !== payoutId) : [...prev, payoutId]
    );
  };

  const handleSelectAll = () => {
    const validPayouts = pendingPayouts.filter((p) => p.iban_valid).map((p) => p.id);
    if (selectedPayouts.length === validPayouts.length) {
      setSelectedPayouts([]);
    } else {
      setSelectedPayouts(validPayouts);
    }
  };

  const handleGenerateSepa = async () => {
    if (selectedPayouts.length === 0) {
      toast.error('Selecione pelo menos um pagamento');
      return;
    }
    if (!executionDate) {
      toast.error('Defina a data de execução');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post('/sepa', { payout_ids: selectedPayouts, execution_date: executionDate });
      if (data.success) {
        toast.success('Ficheiro SEPA gerado', {
          description: `${data.data?.number_of_transactions || 0} transações · €${data.data?.total_amount || 0} · ID: ${data.data?.message_id || ''}`,
        });
        setSelectedPayouts([]);
        fetchData();
      } else {
        throw new Error(data.error || 'Erro ao gerar ficheiro SEPA');
      }
    } catch (error) {
      toast.error('Erro ao gerar ficheiro SEPA', { description: error.response?.data?.error || error.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await api.get(`/sepa/files/${fileId}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SEPA_${fileId}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao fazer download do ficheiro');
    }
  };

  const handleMarkAsSent = async (fileId) => {
    if (!window.confirm('Confirma que o ficheiro foi enviado ao banco?')) return;
    try {
      const { data } = await api.patch(`/sepa/files/${fileId}`, { status: 'sent' });
      if (data.success) {
        toast.success('Ficheiro marcado como enviado');
        fetchData();
      } else {
        throw new Error(data.error || 'Erro ao marcar como enviado');
      }
    } catch (error) {
      toast.error('Erro ao marcar ficheiro como enviado', { description: error.response?.data?.error || error.message });
    }
  };

  const selectedTotal = pendingPayouts
    .filter((p) => selectedPayouts.includes(p.id))
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  if (loading) {
    return (
      <div className="flex h-80 flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">A carregar pagamentos SEPA...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 md:p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Gestão de Pagamentos SEPA</h1>
        <Button onClick={fetchData} variant="outline">
          <RefreshCcw className="mr-2 h-4 w-4" /> Atualizar
        </Button>
      </div>

      {summary && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pagamentos Pendentes</p>
                  <p className="text-2xl font-bold">{summary.total || 0}</p>
                </div>
                <Users className="h-10 w-10 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">IBANs Válidos</p>
                  <p className="text-2xl font-bold text-emerald-600">{summary.valid || 0}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">IBANs Inválidos</p>
                  <p className="text-2xl font-bold text-destructive">{summary.invalid || 0}</p>
                </div>
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total a Pagar</p>
                  <p className="text-2xl font-bold">€{(summary.total_amount || 0).toFixed(2)}</p>
                </div>
                <Euro className="h-10 w-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Gerar Ficheiro SEPA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Data de Execução</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={executionDate}
                  onChange={(e) => setExecutionDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Selecionados</label>
              <div className="flex h-9 items-center gap-3 rounded-md border bg-muted px-3 text-sm">
                <span><strong>{selectedPayouts.length}</strong> pagamentos</span>
                <span className="text-emerald-600"><strong>€{selectedTotal.toFixed(2)}</strong></span>
              </div>
            </div>
          </div>
          <Button
            onClick={handleGenerateSepa}
            disabled={generating || selectedPayouts.length === 0}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            size="lg"
          >
            <FileText className="mr-2 h-5 w-5" />
            {generating ? 'A gerar...' : 'Gerar Ficheiro SEPA'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <CardTitle>Pagamentos Pendentes ({pendingPayouts.length})</CardTitle>
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            {selectedPayouts.length === pendingPayouts.filter((p) => p.iban_valid).length ? 'Desselecionar Todos' : 'Selecionar Todos Válidos'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-3 pl-4 font-medium text-muted-foreground">Sel.</th>
                  <th className="py-3 font-medium text-muted-foreground">Motorista</th>
                  <th className="py-3 font-medium text-muted-foreground">IBAN</th>
                  <th className="py-3 font-medium text-muted-foreground">Valor</th>
                  <th className="py-3 font-medium text-muted-foreground">Data</th>
                  <th className="py-3 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayouts.map((payout) => (
                  <tr key={payout.id} className={`border-b last:border-0 ${!payout.iban_valid ? 'bg-destructive/5' : ''}`}>
                    <td className="py-4 pl-4">
                      <input
                        type="checkbox"
                        checked={selectedPayouts.includes(payout.id)}
                        onChange={() => handleSelectPayout(payout.id)}
                        disabled={!payout.iban_valid}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="py-4">{payout.first_name} {payout.last_name}</td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        {payout.iban || <span className="text-destructive text-xs font-semibold">Não configurado</span>}
                        {payout.iban && (
                          payout.iban_valid
                            ? <CheckCircle className="h-4 w-4 text-emerald-600" />
                            : <AlertCircle className="h-4 w-4 text-destructive" />
                        )}
                      </div>
                    </td>
                    <td className="py-4 font-semibold">€{parseFloat(payout.amount).toFixed(2)}</td>
                    <td className="py-4 text-muted-foreground">{new Date(payout.created_at).toLocaleDateString('pt-PT')}</td>
                    <td className="py-4">
                      <Badge variant="warning">Pendente</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ficheiros SEPA Gerados ({sepaFiles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-3 pl-4 font-medium text-muted-foreground">ID Mensagem</th>
                  <th className="py-3 font-medium text-muted-foreground">Transações</th>
                  <th className="py-3 font-medium text-muted-foreground">Total</th>
                  <th className="py-3 font-medium text-muted-foreground">Data Execução</th>
                  <th className="py-3 font-medium text-muted-foreground">Status</th>
                  <th className="py-3 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sepaFiles.map((file) => (
                  <tr key={file.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-4 pl-4 font-mono text-xs">{file.message_id}</td>
                    <td className="py-4">{file.number_of_transactions}</td>
                    <td className="py-4 font-semibold">€{parseFloat(file.total_amount).toFixed(2)}</td>
                    <td className="py-4">{new Date(file.execution_date).toLocaleDateString('pt-PT')}</td>
                    <td className="py-4">
                      <Badge variant={statusVariant[file.status] || 'outline'} className="gap-1">
                        {getStatusIcon(file.status)}
                        <span className="capitalize">{file.status}</span>
                      </Badge>
                    </td>
                    <td className="py-4">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDownload(file.id)}>
                          <Download className="mr-2 h-4 w-4" /> Download
                        </Button>
                        {file.status === 'generated' && (
                          <Button size="sm" onClick={() => handleMarkAsSent(file.id)}>
                            <Send className="mr-2 h-4 w-4" /> Marcar Enviado
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
